import {
  ALL_FORMATS,
  BlobSource,
  BufferTarget,
  Conversion,
  Input,
  Output,
  WavOutputFormat,
} from 'mediabunny'
import { UFile } from '@w/utils'

export type ToPcmWavOptions = {
  sampleRate?: number
  numberOfChannels?: number
}

const DEFAULT_SAMPLE_RATE = 16_000
const DEFAULT_NUMBER_OF_CHANNELS = 1

/** Converts browser-recorded audio into a PCM WAV without a server round-trip. */
export async function toPcmWav(
  file: UFile,
  options: ToPcmWavOptions = {},
): Promise<UFile> {
  const sampleRate = options.sampleRate ?? DEFAULT_SAMPLE_RATE
  const numberOfChannels =
    options.numberOfChannels ?? DEFAULT_NUMBER_OF_CHANNELS
  const input = new Input({
    source: new BlobSource(file.blob),
    formats: ALL_FORMATS,
  })

  try {
    const durationMs =
      _durationMs(file.meta) ?? Math.round((await input.computeDuration()) * 1000)
    const target = new BufferTarget()
    const output = new Output({
      format: new WavOutputFormat(),
      target,
    })
    const conversion = await Conversion.init({
      input,
      output,
      audio: {
        codec: 'pcm-s16',
        sampleRate,
        numberOfChannels,
        forceTranscode: true,
      },
    })

    await conversion.execute()
    if (!target.buffer) throw new Error('PCM WAV conversion produced no audio')

    return new UFile(new Blob([target.buffer]), {
      path: _wavPath(file.path),
      type: `audio/wav; codecs=audio/pcm; samplerate=${sampleRate}`,
      meta: {
        ...file.meta,
        durationMs,
      },
    })
  } finally {
    input.dispose()
  }
}

function _durationMs(meta: Record<string, unknown> | undefined): number | undefined {
  const durationMs = meta?.durationMs
  return typeof durationMs === 'number' && Number.isFinite(durationMs)
    ? durationMs
    : undefined
}

function _wavPath(path: string): string {
  const withoutExtension = path.replace(/\.[^./]+$/u, '')
  return `${withoutExtension || 'recording'}.wav`
}
